import { format } from 'date-fns';
import { BuildingOfficeIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { BalanceSheet } from '../../services/reports';

interface BalanceSheetReportProps {
  data: BalanceSheet | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}

export default function BalanceSheetReport({
  data,
  loading,
  error,
  onReload,
}: BalanceSheetReportProps) {
  if (loading) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
            <p className="mt-2 text-sm text-slate-600">Loading balance sheet...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="text-center">
            <p className="text-red-600">{error}</p>
            <button
              onClick={onReload}
              className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-cyan-700 bg-cyan-100 hover:bg-indigo-200"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const asOfDate = (data as any).asOfDate || data.reportDate || new Date();

  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="px-6 py-8 sm:px-8">
        {/* Header */}
        <div className="text-center border-b border-gray-200 pb-6 mb-8">
          <h2 className="text-2xl font-bold text-slate-900">Balance Sheet</h2>
          <p className="text-sm text-slate-600 mt-2">
            As of {format(new Date(asOfDate), 'MMMM dd, yyyy')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Assets */}
          <div>
            <h3 className="text-xl font-semibold text-slate-900 mb-6 flex items-center">
              <BuildingOfficeIcon className="h-6 w-6 text-cyan-600 mr-2" />
              Assets
            </h3>

            <div className="mb-8">
              <h4 className="text-lg font-medium text-gray-800 mb-4 bg-cyan-50 px-4 py-2 rounded">Current Assets</h4>
              <div className="space-y-3 pl-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Cash</span>
                  <span className="font-medium">R{(data.assets?.currentAssets?.cash || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Inventory</span>
                  <span className="font-medium">R{(data.assets?.currentAssets?.inventory || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Accounts Receivable</span>
                  <span className="font-medium">R{(data.assets?.currentAssets?.accountsReceivable || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Bird Stock</span>
                  <span className="font-medium">R{(data.assets?.currentAssets?.birdStock || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Feed Stock</span>
                  <span className="font-medium">R{(data.assets?.currentAssets?.feedStock || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Supplies</span>
                  <span className="font-medium">R{(data.assets?.currentAssets?.supplies || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2">
                  <div className="flex justify-between items-center font-semibold">
                    <span className="text-slate-900">Total Current Assets</span>
                    <span>R{(data.assets?.currentAssets?.total || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h4 className="text-lg font-medium text-gray-800 mb-4 bg-cyan-50 px-4 py-2 rounded">Fixed Assets</h4>
              <div className="space-y-3 pl-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Equipment</span>
                  <span className="font-medium">R{(data.assets?.fixedAssets?.equipment || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Aviaries</span>
                  <span className="font-medium">R{(data.assets?.fixedAssets?.aviaries || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Breeding Cages</span>
                  <span className="font-medium">R{(data.assets?.fixedAssets?.breedingCages || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Land & Buildings</span>
                  <span className="font-medium">R{(data.assets?.fixedAssets?.landBuildings || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2">
                  <div className="flex justify-between items-center font-semibold">
                    <span className="text-slate-900">Total Fixed Assets</span>
                    <span>R{(data.assets?.fixedAssets?.total || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-100 p-4 rounded-lg">
              <div className="flex justify-between items-center text-xl font-bold text-blue-900">
                <span>TOTAL ASSETS</span>
                <span>R{(data.assets?.totalAssets || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Liabilities & Equity */}
          <div>
            <h3 className="text-xl font-semibold text-slate-900 mb-6 flex items-center">
              <CurrencyDollarIcon className="h-6 w-6 text-red-600 mr-2" />
              Liabilities & Equity
            </h3>

            <div className="mb-8">
              <h4 className="text-lg font-medium text-gray-800 mb-4 bg-red-50 px-4 py-2 rounded">Current Liabilities</h4>
              <div className="space-y-3 pl-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Accounts Payable</span>
                  <span className="font-medium">R{(data.liabilities?.currentLiabilities?.accountsPayable || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Short-term Debt</span>
                  <span className="font-medium">R{(data.liabilities?.currentLiabilities?.shortTermDebt || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Accrued Expenses</span>
                  <span className="font-medium">R{(data.liabilities?.currentLiabilities?.accruedExpenses || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2">
                  <div className="flex justify-between items-center font-semibold">
                    <span className="text-slate-900">Total Current Liabilities</span>
                    <span>R{(data.liabilities?.currentLiabilities?.total || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h4 className="text-lg font-medium text-gray-800 mb-4 bg-red-50 px-4 py-2 rounded">Long-term Liabilities</h4>
              <div className="space-y-3 pl-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Long-term Debt</span>
                  <span className="font-medium">R{(data.liabilities?.longTermLiabilities?.longTermDebt || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2">
                  <div className="flex justify-between items-center font-semibold">
                    <span className="text-slate-900">Total Long-term Liabilities</span>
                    <span>R{(data.liabilities?.longTermLiabilities?.total || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-red-100 p-4 rounded-lg mb-8">
              <div className="flex justify-between items-center text-lg font-bold text-red-900">
                <span>Total Liabilities</span>
                <span>R{(data.liabilities?.totalLiabilities || 0).toLocaleString()}</span>
              </div>
            </div>

            <div className="mb-8">
              <h4 className="text-lg font-medium text-gray-800 mb-4 bg-green-50 px-4 py-2 rounded">Owner&apos;s Equity</h4>
              <div className="space-y-3 pl-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Owner&apos;s Equity</span>
                  <span className="font-medium">R{(data.equity?.ownerEquity || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Retained Earnings</span>
                  <span className="font-medium">R{(data.equity?.retainedEarnings || 0).toLocaleString()}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2">
                  <div className="flex justify-between items-center font-semibold">
                    <span className="text-slate-900">Total Equity</span>
                    <span>R{(data.equity?.total || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-100 p-4 rounded-lg">
              <div className="flex justify-between items-center text-xl font-bold text-green-900">
                <span>TOTAL LIABILITIES & EQUITY</span>
                <span>R{((data.liabilities?.totalLiabilities || 0) + (data.equity?.total || 0)).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Balance Verification */}
        <div className="mt-8 p-4 bg-slate-50/80 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-slate-700">Balance Verification:</span>
            <span className={`text-sm font-semibold ${
              data.assets?.totalAssets === data.totalLiabilitiesEquity ? 'text-green-600' : 'text-red-600'
            }`}>
              {data.assets?.totalAssets === data.totalLiabilitiesEquity
                ? '✓ Balance Sheet is balanced'
                : '⚠ Balance Sheet is not balanced'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
